// Internal Path -> Localized Slug Matrix
export const slugs = {
    'about-us': {
        tr: 'hakkimizda',
        en: 'about-us',
        ar: 'mn-nahn'
    },
    'contact-us': {
        tr: 'iletisim',
        en: 'contact-us',
        ar: 'at-tasal-bina'
    },
    'blog': {
        tr: 'blog',
        en: 'blog',
        ar: 'al-mudawana'
    },
    'doctors': {
        tr: 'doktorlar',
        en: 'doctors',
        ar: 'al-atibba'
    },
    'categories': {
        tr: 'kategoriler',
        en: 'categories',
        ar: 'tasnifat'
    },
    'services': {
        tr: 'tedaviler',
        en: 'treatments',
        ar: 'khadamatna'
    },
    'book-now': {
        tr: 'randevu-al',
        en: 'book-now',
        ar: 'ahjaz-mawuead'
    },
    'faq': {
        tr: 'sss',
        en: 'faq',
        ar: 'al-asila'
    },
    'privacy-policy': {
        tr: 'gizlilik-politikasi',
        en: 'privacy-policy',
        ar: 'siyasat-al-khususia'
    },
    'yasal': {
        tr: 'yasal',
        en: 'legal',
        ar: 'qanuni'
    },
    'kvkk': {
        tr: 'kvkk',
        en: 'kvkk',
        ar: 'kvkk'
    },
    'cookie-policy': {
        tr: 'cerez-politikasi',
        en: 'cookie-policy',
        ar: 'cookie-policy' // Approximate fallback or keep EN if unknown, using standard 'policiy-cookie' or similar. Let's use English fallback for AR for safety if unsure, or simple transliteration.
    },
    'ticari-elektronik-ileti-izni': {
        tr: 'ticari-elektronik-ileti-izni',
        en: 'commercial-electronic-consent',
        ar: 'commercial-electronic-consent'
    },
    'veri-isleme-politikasi': {
        tr: 'veri-isleme-politikasi',
        en: 'data-processing-policy',
        ar: 'data-processing-policy'
    },
    'veri-imha-politikasi': {
        tr: 'veri-imha-politikasi',
        en: 'data-destruction-policy',
        ar: 'data-destruction-policy'
    },
    'yetki-belgesi': {
        tr: 'yetki-belgesi',
        en: 'authorization-certificate',
        ar: 'shahadat-al-tarkhis'
    }
} as const;

export type PageKey = keyof typeof slugs;

export function getLocalizedPath(internalPath: string, lang: 'tr' | 'en' | 'ar') {
    // Strip leading slash
    const key = internalPath.replace(/^\//, '') as PageKey;
    if (slugs[key] && slugs[key][lang]) {
        // If TR (default), return /slug
        if (lang === 'tr') {
            return `/${slugs[key][lang]}`;
        }
        // Otherwise /lang/slug
        return `/${lang}/${slugs[key][lang]}`;
    }
    // Fallback or Home
    if (lang === 'tr') return internalPath.startsWith('/') ? internalPath : `/${internalPath}`;
    return `/${lang}${internalPath.startsWith('/') ? internalPath : `/${internalPath}`}`;
}

// Tam yerellestirilmis path uret: parent (lang prefix + slug) + opsiyonel cocuk slug.
// Or: getLocalizedRoutePath('services', 'en', 'foo') -> '/en/treatments/foo'
export function getLocalizedRoutePath(
    key: PageKey,
    lang: 'tr' | 'en' | 'ar',
    childSlug?: string
) {
    const base = getLocalizedPath(key, lang);
    return childSlug ? `${base}/${childSlug}` : base;
}

// TERS arama: verilen slug + dile karsilik gelen route key'i bul. Yoksa null.
// Or: getInternalPath('tedaviler', 'tr') -> 'services'
export function getInternalPath(
    slug: string,
    lang: 'tr' | 'en' | 'ar'
): PageKey | null {
    for (const key of Object.keys(slugs) as PageKey[]) {
        if (slugs[key][lang] === slug) return key;
    }
    return null;
}


// Admin'den girilen ic linkleri (orn. "/book-now", "/en/services",
// "/tedaviler/") sayfanin diline ve kanonik slug'larina cevirir:
// "/book-now" (tr) -> "/randevu-al", "/en/services" -> "/en/treatments".
// Dis linkler, #capa ve tanimsiz segmentler oldugu gibi korunur.
const LEGACY_SEGMENT_ALIASES: Record<string, PageKey> = { services: 'services' };

export function localizeInternalHref(href: string | null | undefined, lang: 'tr' | 'en' | 'ar'): string {
    const value = (href || '').trim();
    if (!value.startsWith('/') || value.startsWith('//')) return value;

    const [pathPart, rest = ''] = value.split(/(?=[?#])/);
    const segments = pathPart.split('/').filter(Boolean);
    if (segments[0] === 'tr' || segments[0] === 'en' || segments[0] === 'ar') segments.shift();

    const localized = segments.map((segment) => {
        const key =
            (Object.keys(slugs) as PageKey[]).find((k) =>
                k === segment || (Object.values(slugs[k]) as string[]).includes(segment)
            ) || LEGACY_SEGMENT_ALIASES[segment];
        return key ? slugs[key][lang] : segment;
    });

    const path = `/${localized.join('/')}`;
    const prefixed = lang === 'tr' ? path : `/${lang}${path === '/' ? '' : path}`;
    return `${prefixed}${rest}`;
}
